import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { components } from "../api/schema";
import { useHostBrand } from "../shell/hostbrand";
import { BrandFoot, BrandLockup, useBrand } from "../shell/brand";
import { AcceptPending, AcceptRefused, beginAccept, browser, type SignInProvider } from "../shell/provider";
import { useSession } from "../shell/session";
import { ProblemBox } from "../ui";
import { useSignInDoors } from "./doors";

/** Where an invitee lands from the link in their mail (#506). The address the
 * invitation email carries is this page's, and the token rides in the query
 * because that is what a link from a mail client can carry. */
export const ACCEPT_PATH = "/accept";

/** Restates ADR-0142 decision 2 and ADR-0174 decisions 1 and 3 — where the deployment names an
 * identity provider the door is the invitation's scope's, never the invitee's: its organization's
 * sign-in where it has one, and otherwise an account at the sign-in page whose password the
 * invitee sets there, which this console never sees. */
const LEAD = "You have been invited. Accepting signs you in through your organization's sign-in or, where it has none, lets you set a password on the sign-in page — and creates your access.";

/** Restates ADR-0466 decisions 1a, 3a and 5a — where the deployment names no identity provider the
 * invitee sets a password this console holds, on this page; it is temporary, and a second
 * invitation comes once the console's sign-in moves to an identity provider. */
export const ACCEPT_PASSWORD_LEAD = "You have been invited. You accept by setting a password for this console, and accepting signs you in. The password is temporary: when this console's sign-in moves to an identity provider, you will receive a second invitation and sign in that way from then on.";

/** Restates ADR-0466 decision 3a — an address that already holds a password on this console is
 * accepted with that password, and the invitation is added to that account. */
export const ACCEPT_EXISTING_PASSWORD = "This address already has a password for this console. Enter it to add this invitation to that account.";

/** The least a password set here may be: passwd.MinLength, the rule `useradd` and `passwd` hold
 * to, which the acceptance enforces too (ADR-0466 decision 3a). Said here so the person is told
 * before they submit; the server's refusal stays the rule. */
const PASSWORD_MIN = 12;

/** The invitee's organization at the provider is not made yet (ADR-0171 decision 8a). */
export const ACCEPT_PENDING = "This invitation cannot be accepted just yet: its sign-in is still being set up. Try again in a few minutes.";

/** The one sentence a refused acceptance gets, for the reason PROVIDER_REFUSED
 * has one (ZENITH.md §6.3): an invitation that is expired, revoked, already
 * accepted, or proved through a method its scope does not accept is refused
 * the same way one that never existed is, and naming which would answer a
 * question the 404 rule exists to leave unanswered. */
export const ACCEPT_REFUSED = "That invitation cannot be accepted. It may have been used already, revoked, or expired. Ask whoever invited you to send another.";

/** Nothing to accept: the link carried no token at all, which is a link
 * somebody truncated rather than an invitation Zenith refused. Said
 * separately because the person can act on it — find the whole link. */
export const ACCEPT_NO_TOKEN = "This link is missing its invitation. Open the whole link from your invitation email, without cutting it short.";

/** Restates ZENITH.md §6.3 — the link was opened and nothing was sent, which is true of any token;
 * whether this one can be accepted is the acceptance's to answer, and only it says. */
export const ACCEPT_SIGNED_IN = "You opened an invitation link while signed in. Opening it used nothing: sign out, and this link opens its acceptance page.";

/** Restates ADR-0142 decision 3 — an acceptance links whoever the sign-in proves, and signing out
 * of Zenith leaves the browser signed in at the sign-in page, so the account is the person's to pick. */
export const ACCEPT_SIGNED_IN_WHO = "When the sign-in page asks, sign in as the person the invitation was sent to, or open the link in a private window.";

/** Where an invitation link lands in a browser that holds a session (#1375).
 *
 * The acceptance page is anonymous, so a signed-in browser was handed the
 * console's *Not found* — and an invitee who is already an operator, which is
 * every staff invitation, read that the link was broken. This page says what
 * happened and offers the route: sign out through `POST /v1/auth/logout`, the
 * same call the account menu makes, and the address — token and all — is the
 * acceptance page's once the session is gone. **It calls nothing about the
 * invitation**: the token is never sent from here, so the page cannot say
 * whether it is good, and an acceptance stays the one place that answers. */
export function AcceptSignedIn() {
  const { me, signOut } = useSession();
  const host = useHostBrand();
  const branded = host && host.source === "organization" ? host : undefined;
  useBrand(branded, "host");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const leave = async () => {
    setBusy(true);
    // The session's own sign-out: it ends the session and forgets the scope,
    // and App then draws the acceptance page at this same address. A session
    // minted from an identity first ends its session at the provider, and the
    // signed-out page returns here (ADR-0261 decision 4a), so the acceptance
    // proves whoever the invitee signs in as, and not the last person.
    await signOut({ returnTo: location.pathname + location.search });
  };
  return (
    <main className="gate">
      <div className="gate-card form">
        {branded && <div className="gate-brand"><BrandLockup brand={branded} source="host" /></div>}
        <div>
          <h1>You are signed in</h1>
          <p className="muted">{ACCEPT_SIGNED_IN}</p>
        </div>
        {me && <p>Signed in as <strong>{me.user.email}</strong>.</p>}
        <p className="muted">{ACCEPT_SIGNED_IN_WHO}</p>
        <button type="button" className="btn btn-primary gate-provider" disabled={busy} onClick={() => void leave()}>
          Sign out and continue
        </button>
        <Link to="/">Stay signed in and go to the console</Link>
        {branded && <BrandFoot brand={branded} />}
      </div>
    </main>
  );
}

/** The invitee-facing gate. Where the deployment names an identity provider it
 * is one button and no form: no password is typed on this page (ADR-0174). The
 * button asks Zenith where to go — the scope decides, and this page is not
 * told which scope — and Zenith answers with the provider's page: a sign-in,
 * or the step where a hosted invitee sets their own password.
 *
 * **Where the deployment names none, the password is typed here** (ADR-0466):
 * the button asks Zenith which password to ask for, and the form that follows
 * sends it with the token. That door is the deployment's, never the page's
 * guess: the first call decides it, and refuses a password on any other. */
export default function Accept() {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const { provider, loaded } = useSignInDoors();
  const host = useHostBrand();
  const branded = host && host.source === "organization" ? host : undefined;
  useBrand(branded, "host");
  const [busy, setBusy] = useState(false);
  // A refusal carried back from the callback, which is where the acceptance
  // is actually made: the provider returns the browser to Login's callback
  // path, and it sends the person back here with this when the accept was
  // refused.
  const refused = (location.state as { refused?: boolean } | null)?.refused === true;
  const [notice, setNotice] = useState<string>();

  const go = async (p: SignInProvider) => {
    setBusy(true); setNotice(undefined);
    try {
      browser.assign(await beginAccept(p, token, ACCEPT_PATH + location.search));
    } catch (e) {
      setNotice(e instanceof AcceptRefused ? ACCEPT_REFUSED
        : e instanceof AcceptPending ? ACCEPT_PENDING
          : "The sign-in is not answering right now. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <main className="gate">
      <div className="gate-card form">
        {branded && <div className="gate-brand"><BrandLockup brand={branded} source="host" /></div>}
        <div>
          <h1>Accept your invitation</h1>
          {(provider || !loaded) && <p className="muted">{LEAD}</p>}
          {!provider && loaded && <p className="muted">{ACCEPT_PASSWORD_LEAD}</p>}
        </div>
        {refused && <div role="alert" className="problem"><div><strong>{ACCEPT_REFUSED}</strong></div></div>}
        {notice && <div role="alert" className="problem"><div><strong>{notice}</strong></div></div>}
        {!token
          ? <div role="alert" className="problem"><div><strong>{ACCEPT_NO_TOKEN}</strong></div></div>
          : provider
            ? (
              <button type="button" className="btn btn-primary gate-provider" disabled={busy} onClick={() => void go(provider)}>
                Accept and continue
              </button>
            )
            /* The deployment names no provider: the password door (ADR-0466).
               Drawn only once the deployment has answered, so a question still
               in flight is never taken for an answer. */
            : loaded && <PasswordDoor token={token} />}
        {branded && <BrandFoot brand={branded} />}
      </div>
    </main>
  );
}

type AcceptStep = components["schemas"]["InvitationAcceptStepStep"];
type Problem = components["schemas"]["Problem"];

/** The password door's two calls (ADR-0466 decision 3a), through
 * `POST /v1/invitations:accept` like every other door. The first sends the
 * token alone and is told which password to ask for: a new one, or the one an
 * account at the invitation's address already holds. The second sends the
 * token and the password; the acceptance sets the session cookie, and the
 * shell then opens on the person's new access.
 *
 * **The password is held in this component's state and in the one request
 * that carries it**, and nowhere else: never stored, never put in the address,
 * never logged. */
function PasswordDoor({ token }: { token: string }) {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState<AcceptStep>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<Problem>();

  const refusedOr = (status: number) =>
    status === 404 ? ACCEPT_REFUSED : "This invitation cannot be accepted here yet. Ask whoever invited you to try again.";

  const begin = async () => {
    setBusy(true); setNotice(undefined); setProblem(undefined);
    const { data, response } = await api.POST("/invitations:accept", { body: { token } });
    setBusy(false);
    if (response.status === 200 && data && (data.step === "new_password" || data.step === "existing_password")) {
      setStep(data.step);
      return;
    }
    setNotice(refusedOr(response.status));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setNotice(undefined); setProblem(undefined);
    if (step === "new_password" && password.length < PASSWORD_MIN) {
      setNotice(`Choose a password of at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (step === "new_password" && password !== confirm) {
      setNotice("The two passwords are not the same.");
      return;
    }
    setBusy(true);
    const { error, response } = await api.POST("/invitations:accept", { body: { token, password } });
    if (response.ok) {
      setPassword(""); setConfirm("");
      navigate("/", { replace: true });
      await refresh();
      return;
    }
    setBusy(false);
    if (response.status === 404) { setNotice(ACCEPT_REFUSED); return; }
    setProblem((error as Problem | undefined) ?? { reason: "internal", message: `The invitation was not accepted (${response.status}).`, request_id: "" });
  };

  return (
    <>
      {notice && <div role="alert" className="problem"><div><strong>{notice}</strong></div></div>}
      {problem && <ProblemBox problem={problem} />}
      {!step
        ? (
          <button type="button" className="btn btn-primary gate-provider" disabled={busy} onClick={() => void begin()}>
            Accept and continue
          </button>
        )
        : (
          <form className="form" onSubmit={(e) => void submit(e)}>
            {step === "existing_password" && <p className="muted">{ACCEPT_EXISTING_PASSWORD}</p>}
            <div className="field">
              <label htmlFor="accept-password">{step === "new_password" ? "Choose a password" : "Your password"}</label>
              <input id="accept-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete={step === "new_password" ? "new-password" : "current-password"}
                minLength={step === "new_password" ? PASSWORD_MIN : undefined} />
              {step === "new_password" && <p className="muted">At least {PASSWORD_MIN} characters.</p>}
            </div>
            {step === "new_password" && (
              <div className="field">
                <label htmlFor="accept-password-confirm">The same password again</label>
                <input id="accept-password-confirm" type="password" autoComplete="new-password" required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} />
              </div>
            )}
            <div className="actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {step === "new_password" ? "Set password and accept" : "Accept"}
              </button>
            </div>
          </form>
        )}
    </>
  );
}
